import React from 'react';
import { Col, Row } from 'react-grid-system';
import Divider from '@Components/Divider/Divider';
import Accordion from '@Components/Accordion/Accordion';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import useTranslate from '@Utils/hooks/useTranslate';
import LabeledInformation from './LabeledInformation';
import { AccordionContentWrapper, SectionHeader } from '@Components/Card/cards/ShippingDetails/ShippingDetails.styled';
import MapDetails from './MapDetails';
import { IAddress } from '@ApiModels/commerceProfile';

interface IPrepaidProductDelivery {
	orderWithPrepaid: boolean;
	selectedAddress: IAddress | undefined;
	showMap: boolean;
	subtitle: string;
}

const PrepaidProductDelivery = ({
	orderWithPrepaid,
	selectedAddress,
	showMap,
	subtitle,
}: IPrepaidProductDelivery): JSX.Element => {
	const { translate } = useTranslate();

	return (
		<Accordion
			subtitleColor={orderWithPrepaid ? 'black54' : 'black87'}
			isPrepaid={orderWithPrepaid}
			title={translate('sim-order-details.accordion.shipping-data.title')}
			subtitle={subtitle}
			icon="locationPin"
		>
			<AccordionContentWrapper>
				<Divider withoutLine marginTop={40} />
				<SectionHeader>{translate('shipping-details.accordion.billing-data.section-header')}</SectionHeader>
				<Divider marginBottom={8} />
				<Row>
					<Col xs={12} sm={6}>
						<ConditionalRender
							show={!!selectedAddress?.governorate}
							onTrue={() => (
								<LabeledInformation
									label={translate('shipping-details.billing.contact-data.data.governorate')}
									data={selectedAddress?.governorate}
								/>
							)}
						/>
						<ConditionalRender
							show={!!selectedAddress?.wilayat}
							onTrue={() => (
								<LabeledInformation
									label={translate('shipping-details.billing.contact-data.data.wilayat')}
									data={selectedAddress?.wilayat}
								/>
							)}
						/>
						<ConditionalRender
							show={!!selectedAddress?.city}
							onTrue={() => (
								<LabeledInformation
									label={
										!!selectedAddress?.area
											? translate('shipping-details.billing.contact-data.data.city-area')
											: translate('shipping-details.billing.contact-data.data.city')
									}
									data={
										!!selectedAddress?.area
											? `${selectedAddress?.city} / ${selectedAddress?.area}`
											: selectedAddress?.city
									}
								/>
							)}
						/>
						<ConditionalRender
							show={!!selectedAddress?.way}
							onTrue={() => (
								<LabeledInformation
									label={translate('shipping-details.billing.contact-data.data.way')}
									data={selectedAddress?.way}
								/>
							)}
						/>
						<ConditionalRender
							show={!!selectedAddress?.house}
							onTrue={() => (
								<LabeledInformation
									label={translate('deliver-address.text-field.house-info')}
									data={
										selectedAddress?.house ??
										'' +
											(selectedAddress?.house && selectedAddress?.apartment
												? '/' + selectedAddress.apartment
												: '')
									}
								/>
							)}
						/>
					</Col>

					<MapDetails selectedAddress={selectedAddress} showMap={showMap} />
				</Row>
			</AccordionContentWrapper>
		</Accordion>
	);
};

export default PrepaidProductDelivery;
